import { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { DollarSign, Users, TrendingUp, Repeat, Music2, ShoppingBag, Sparkles, Target } from "lucide-react";

export default function GrowthDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/dashboard/growth").then((r) => { setData(r.data); setLoading(false); }); }, []);

  if (loading) return <div className="text-white/40">Loading growth metrics…</div>;
  if (!data) return <div className="text-red-400">Failed to load metrics.</div>;

  const kpis = [
    { icon: DollarSign, label: "Daily Revenue", value: `$${data.daily_revenue.toFixed(2)}`, sub: `${data.daily_orders} orders`, tid: "kpi-daily-revenue" },
    { icon: DollarSign, label: "Monthly Revenue", value: `$${data.monthly_revenue.toFixed(2)}`, sub: `${data.monthly_orders} orders`, tid: "kpi-monthly-revenue" },
    { icon: Repeat, label: "MRR", value: `$${data.mrr.toFixed(2)}`, sub: `${data.active_subscribers} active subs`, tid: "kpi-mrr" },
    { icon: Users, label: "Active Members", value: data.active_members, sub: `${data.total_members} total`, tid: "kpi-active-members" },
    { icon: Music2, label: "Music Sales", value: `$${data.music_sales.toFixed(2)}`, tid: "kpi-music-sales" },
    { icon: ShoppingBag, label: "Merch Sales", value: `$${data.merch_sales.toFixed(2)}`, tid: "kpi-merch-sales" },
    { icon: Sparkles, label: "Affiliate Sales", value: `$${data.affiliate_sales.toFixed(2)}`, tid: "kpi-affiliate-sales" },
    { icon: Target, label: "Conversion", value: `${data.conversion_rate}%`, sub: `${data.unique_buyers_30d}/${data.unique_visitors_30d}`, tid: "kpi-conversion" },
    { icon: TrendingUp, label: "Referral Revenue", value: `$${data.referral_revenue.toFixed(2)}`, sub: `${data.referral_orders} orders`, tid: "kpi-referral-revenue" },
  ];

  return (
    <div className="space-y-6" data-testid="growth-dashboard">
      <div>
        <h2 className="font-heading text-2xl text-white tracking-wider">Growth Engine</h2>
        <p className="text-white/40 text-xs mt-1">Real-time metrics driven by the entire Luxifer 6 ecosystem.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} data-testid={k.tid} className="bg-[#0A0A0A] border border-white/5 rounded p-4">
            <div className="flex justify-between items-start mb-2">
              <p className="text-[10px] uppercase tracking-widest text-white/40">{k.label}</p>
              <k.icon className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
            <p className="text-2xl text-white font-bold">{k.value}</p>
            {k.sub && <p className="text-white/30 text-[10px] mt-1">{k.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Top Songs" rows={data.top_songs} value="revenue" prefix="$" />
        <Panel title="Top Products" rows={data.top_products} value="revenue" prefix="$" />
        <Panel title="Top Videos" rows={data.top_videos} value="views" suffix=" views" />
        <Panel title="Top Referrers" rows={(data.top_referrers || []).map(r => ({ name: r.display_name || r.username, referrals_count: r.referrals_count, tier: r.tier_slug }))} value="referrals_count" suffix=" refs" showTier />
        <Panel title="Top Cities" rows={data.top_cities} value="count" suffix=" visits" />
        <Panel title="Top Countries" rows={data.top_countries} value="count" suffix=" visits" />
      </div>
    </div>
  );
}

function Panel({ title, rows, value, prefix = "", suffix = "", showTier = false }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded p-4" data-testid={`panel-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{title}</p>
      {(rows || []).length === 0 ? (
        <p className="text-white/30 text-xs">No data yet.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.slice(0, 10).map((r, i) => (
            <div key={i} className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-white/30 text-xs">#{i + 1}</span>
                <span className="text-white truncate">{r.name}</span>
                {showTier && r.tier && <span className="text-[9px] uppercase tracking-widest text-[#D4AF37]">{r.tier}</span>}
              </div>
              <span className="text-[#D4AF37] text-xs font-medium flex-shrink-0 ml-2">{prefix}{r[value] || 0}{suffix}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
